import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import type * as MapboxGL from 'mapbox-gl';
import { Subscription } from 'rxjs';

declare const mapboxgl: typeof MapboxGL;
import { NavigationService } from '../../../modulos/navigation/navigation.service';
import { KmzAGeoJSONService } from '../kmz-a-geojson.service';
import { MapaIconosService } from '../mapa-iconos.service';
import { MapboxService, Tema, Vista } from '../mapbox.service';

export { Vista, Tema } from '../mapbox.service';

// Espera entre reintentos de sincronizar capas mientras el estilo carga.
const ESPERA_ESTILO_MS = 400;
const MAX_REINTENTOS_ESTILO = 50; // ~20s

export type GeometriaSoportada =
  'puntos' | 'cluster-puntos' | 'linea' | 'poligono' | 'heatmap' | 'kmz';

export type ExpresionMapbox = unknown[];

export interface EstiloCapa {
  color?: string | ExpresionMapbox;
  icono?: string | ExpresionMapbox;
  tamanioIcono?: number | ExpresionMapbox;
  // Si el icono es SDF (ej. 'pin-tematico'), Mapbox lo tinta con icon-color.
  colorIcono?: string | ExpresionMapbox;
  // Anclaje del icono al lat/lng. Default: 'bottom' (la punta del pin queda
  // sobre la coordenada).
  iconoAnchor?: 'bottom' | 'center' | 'top' | 'left' | 'right';
  // Icono opcional que se renderea encima del principal (ej. el "disco
  // blanco" detrás del texto). Es otro symbol layer separado, así que se
  // tinta y posiciona independiente.
  iconoSecundario?: string | ExpresionMapbox;
  colorIconoSecundario?: string | ExpresionMapbox;
  // Texto que va sobre el icono (típicamente la inicial de la división).
  texto?: string | ExpresionMapbox;
  colorTexto?: string | ExpresionMapbox;
  tamanioTexto?: number;
  // Offset del texto en em (referido al text-size). Útil para centrarlo en
  // la burbuja del pin teardrop.
  offsetTexto?: [number, number];
  // Solo con `icono`: por debajo de este zoom cada punto se dibuja como un
  // círculo chico (color = `colorIcono`, tamaño = `radio`, borde =
  // `colorBorde`/`grosorBorde`) y el icono aparece recién desde este zoom.
  // Para zonas densas donde los pines se solapan de lejos.
  zoomIcono?: number;
  grosorLinea?: number | ExpresionMapbox;
  opacidad?: number;
  radio?: number | ExpresionMapbox;
  colorBorde?: string;
  grosorBorde?: number;
  // --- Solo para tipo 'heatmap' ---
  // Peso de cada punto en el heatmap (0..1). Típicamente una expresión que
  // normaliza una métrica (ej. maxRssi). Default: 1.
  pesoHeatmap?: number | ExpresionMapbox;
  // Rampa de color del heatmap. Pares [stop, color] aplicados sobre
  // heatmap-density (0..1). Default: rampa azul→rojo de Mapbox. Para la
  // marca INSIDEht se pasa el gradiente RSSI.
  rampaHeatmap?: Array<[number, string]>;
  // Radio en px del kernel a zoom base. Default 30.
  radioHeatmap?: number | ExpresionMapbox;
  // Intensidad global del heatmap. Default 1.
  intensidadHeatmap?: number | ExpresionMapbox;
  // Zoom a partir del cual el heatmap se desvanece (para dar paso a los
  // puntos individuales). Default: sin fade.
  zoomMaximoHeatmap?: number;
}

export interface InteraccionCapa {
  popupHtml?: (props: Record<string, unknown>) => string;
  cursor?: 'pointer' | 'default';
  onClick?: (feature: GeoJSON.Feature) => void;
  /** Abre `popupHtml` también al pasar el mouse (queda abierto al salir, para poder usar sus enlaces). */
  popupAlPasar?: boolean;
}

export interface ConfigCluster {
  radio?: number;
  maxZoom?: number;
  coloresCirculo?: [string, number, string, number, string];
  radiosCirculo?: [number, number, number, number, number];
  // Propiedades acumuladas por cluster (clusterProperties de Mapbox).
  // Ej: { cuentaAlerta: ['+', ['case', ['==', ['get','estado'],'alerta'], 1, 0]] }
  propiedades?: Record<string, ExpresionMapbox>;
  // Override para color del círculo (paint expression). Si no, usa step por count.
  expresionColor?: ExpresionMapbox;
  // Override color del texto del cluster.
  colorTexto?: string | ExpresionMapbox;
}

export interface ConfigCapa {
  id: string;
  tipo: GeometriaSoportada;
  data: GeoJSON.FeatureCollection | { urlKmz: string; urlGeoJson?: string };
  estilo?: EstiloCapa;
  interaccion?: InteraccionCapa;
  cluster?: ConfigCluster;
  visible?: boolean;
  // Mayor = más arriba en z-order. Default 0. Tras cada sync, las capas
  // se reordenan via map.moveLayer() en orden ascendente de prioridad.
  prioridad?: number;
}

interface CapaInternaEstado {
  config: ConfigCapa;
  sourceIds: string[];
  layerIds: string[];
  handlers: Array<{
    tipo: 'click' | 'mouseenter' | 'mouseleave';
    layerId: string;
    fn: (e: unknown) => void;
  }>;
}

/**
 * Primer stop utilizable de una rampa de heatmap. El `heatmap-density` 0
 * SIEMPRE tiene que ser transparente (si no, Mapbox pinta todo el canvas),
 * así que ese stop lo reserva el componente y la rampa del consumidor arranca
 * un épsilon después.
 */
const EPSILON_HEATMAP = 0.01;

/**
 * Deja la rampa lista para concatenar después del stop 0 transparente:
 * ordenada y con los stops estrictamente ascendentes.
 *
 * Mapbox rechaza la capa entera —y con ella el resto del armado de capas— si
 * dos stops repiten valor. Es lo que pasaba con las rampas de cobertura, que
 * definen su primer color en 0.0 y chocaban con el transparente:
 * `... 0, 'rgba(0,0,0,0)', 0, '#FF0000' ...`. El síntoma era despistante: el
 * error se veía en el heatmap y lo que "no funcionaba" era la capa siguiente.
 */
export function normalizarRampaHeatmap(
  rampa: Array<[number, string]>,
): Array<[number, string]> {
  const ordenada = [...(rampa ?? [])].sort((a, b) => a[0] - b[0]);
  const salida: Array<[number, string]> = [];
  for (const [stop, color] of ordenada) {
    const anterior = salida.length ? salida[salida.length - 1][0] : 0;
    // El primer stop no puede pisar el 0 del transparente, y ninguno puede
    // repetir el anterior: se corre lo mínimo para mantener el orden.
    const corregido = stop > anterior ? stop : anterior + EPSILON_HEATMAP;
    salida.push([Math.min(corregido, 1), color]);
  }
  return salida;
}

@Component({
  selector: 'app-mapa-base',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './mapa-base.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mapa-base.component.scss'],
})
export class MapaBaseComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('contenedor', { static: true })
  contenedorRef!: ElementRef<HTMLDivElement>;

  @Input() capas: ConfigCapa[] = [];
  @Input() centro: [number, number] = [-58.38, -34.6];
  @Input() zoom = 8;
  @Input() vista: Vista = 'default';
  @Input() mostrarToggleVista = false;
  @Input() coordenadaMarker?: [number, number];
  @Input() markerArrastrable = false;
  @Input() respetaTema = true;
  // Config props para Mapbox Standard / Standard Satellite via
  // setConfigProperty('basemap', key, value). Ej: showPointOfInterestLabels,
  // showPlaceLabels, show3dObjects, theme, lightPreset (override del auto).
  // El componente padre arma este object y lo refresca cuando el usuario
  // toca el configurador.
  @Input() configBasemap: Record<string, string | boolean | number> = {};

  @Output() mapaListo = new EventEmitter<mapboxgl.Map>();
  @Output() coordenadaCambiada = new EventEmitter<[number, number]>();
  @Output() featureClick = new EventEmitter<{
    capaId: string;
    feature: GeoJSON.Feature;
  }>();
  @Output() vistaCambiada = new EventEmitter<Vista>();
  // Emite el config.id de una capa KMZ cuando terminó de cargar (éxito o
  // error). El padre lo usa para apagar el spinner por-KMZ.
  @Output() kmzCargada = new EventEmitter<string>();

  public mapa?: mapboxgl.Map;

  // El padre lo usa para detectar que sus capas nunca llegaron al mapa y
  // forzar un recompute, en vez de confiar en que el diff de datos alcance.
  public get capasSincronizadas(): boolean {
    return this.capasActivas.size > 0;
  }

  private tema: Tema = 'light';
  private capasActivas = new Map<string, CapaInternaEstado>();
  // Serializa las mutaciones del mapa (sincronizarCapas + reaplicarEstadoEnEstilo).
  // Ambas son async y ambas hacen addSource/addLayer + mutan capasActivas. Sin
  // esto se solapan (ej: KMZ con fetch lento/504 deja el await abierto mientras
  // el polling dispara ngOnChanges→sincronizarCapas) y se duplica el addSource
  // → "There is already a source with ID '...-src'".
  private colaSync: Promise<void> = Promise.resolve();
  private reintentosEstilo = 0;
  private marker?: mapboxgl.Marker;
  private modoOscuro$?: Subscription;
  private resizeObserver?: ResizeObserver;
  // Solo un popup abierto a la vez: cualquier click cierra el anterior.
  private popupActivo?: mapboxgl.Popup;

  constructor(
    private mapboxService: MapboxService,
    private iconosService: MapaIconosService,
    private kmzService: KmzAGeoJSONService,
    private navigationService: NavigationService,
  ) {}

  ngAfterViewInit(): void {
    if (this.respetaTema) {
      this.tema = this.leerTemaInicial();
    }

    const map = this.mapboxService.crearMapa({
      container: this.contenedorRef.nativeElement,
      centro: this.centro,
      zoom: this.zoom,
      vista: this.vista,
      tema: this.tema,
    });
    this.mapa = map;

    map.on('style.load', () =>
      this.encolarSync(() => this.reaplicarEstadoEnEstilo()),
    );
    map.on('load', () => {
      this.mapaListo.emit(map);
      // Un ngOnChanges de `capas` que llegó antes de que existiera la instancia
      // salió por el `if (!this.mapa) return`, y el padre no vuelve a emitir si
      // la data no cambió: sin este sync el mapa se queda sin puntos para
      // siempre. sincronizarCapas es idempotente (mira capasActivas).
      this.encolarSync(() => this.sincronizarCapas());
    });
    // Los PNGs legacy (pin-<division>-<estado>, cluster-<n>) ya no se
    // precargan: si una capa referencia uno, se resuelve on-demand acá.
    map.on('styleimagemissing', (e) => {
      this.iconosService.cargarImagenFaltante(map, (e as { id: string }).id);
    });

    // Si el container cambia de tamaño después de inicializar el mapa
    // (ej. layout responsive, hijos que cargan, modal abriéndose),
    // Mapbox no se reajusta solo. ResizeObserver dispara map.resize().
    this.resizeObserver = new ResizeObserver(() => {
      this.mapa?.resize();
    });
    this.resizeObserver.observe(this.contenedorRef.nativeElement);

    if (this.respetaTema) {
      this.modoOscuro$ = this.navigationService.modoOscuro$.subscribe(
        (oscuro) => {
          const nuevoTema: Tema = oscuro ? 'dark' : 'light';
          if (nuevoTema !== this.tema) {
            this.tema = nuevoTema;
            if (this.vista === 'default') {
              this.aplicarEstilo();
            }
          }
        },
      );
    }

    if (this.coordenadaMarker) {
      this.crearMarker(this.coordenadaMarker, this.markerArrastrable);
    }
  }

  ngOnChanges(cambios: SimpleChanges): void {
    if (!this.mapa) return;

    if (cambios['vista'] && !cambios['vista'].firstChange) {
      this.aplicarEstilo();
    }
    if (cambios['centro'] && !cambios['centro'].firstChange) {
      this.mapa.setCenter(this.centro);
    }
    if (cambios['zoom'] && !cambios['zoom'].firstChange) {
      this.mapa.setZoom(this.zoom);
    }
    if (cambios['capas']) {
      this.encolarSync(() => this.sincronizarCapas());
    }
    if (cambios['configBasemap'] && !cambios['configBasemap'].firstChange) {
      this.aplicarConfigBasemap();
    }
    if (
      cambios['coordenadaMarker'] &&
      !cambios['coordenadaMarker'].firstChange
    ) {
      if (this.coordenadaMarker) {
        if (this.marker) {
          this.marker.setLngLat(this.coordenadaMarker);
        } else {
          this.crearMarker(this.coordenadaMarker, this.markerArrastrable);
        }
      } else if (this.marker) {
        this.marker.remove();
        this.marker = undefined;
      }
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.modoOscuro$?.unsubscribe();
    this.marker?.remove();
    this.mapa?.remove();
  }

  public alternarVista(): void {
    const nueva: Vista = this.vista === 'default' ? 'satelite' : 'default';
    this.vista = nueva;
    this.aplicarEstilo();
    this.vistaCambiada.emit(nueva);
  }

  // API pública: usar siempre este método para abrir popups, garantiza que solo
  // haya uno a la vez. Quien lo llame puede pasar HTML string o un nodo DOM
  // (para popups Angular dinámicos). Devuelve la instancia del popup.
  // `offsetPx` separa el popup del lat/lng — útil para que no tape el pin.
  public mostrarPopup(opciones: {
    coordenadas: [number, number];
    html?: string;
    domContent?: HTMLElement;
    offsetPx?: number;
    onClose?: () => void;
    allowMultiple?: boolean;
  }): mapboxgl.Popup | undefined {
    if (!this.mapa) return undefined;
    if (!opciones.allowMultiple) this.cerrarPopupActivo();

    const popup = new mapboxgl.Popup({
      closeButton: true,
      maxWidth: '460px',
      // Mapbox auto-elige anchor (top/bottom/left/right). Un offset numérico
      // se aplica como distancia a lo largo del eje del anchor — el popup
      // siempre queda a `offsetPx` del lat/lng en la dirección que evite
      // taparlo.
      offset: opciones.offsetPx ?? 0,
      // En modo multi-popup, desactivar closeOnClick para que un click en otro
      // marker no cierre los popups existentes antes de que abra el nuevo.
      closeOnClick: !opciones.allowMultiple,
    }).setLngLat(opciones.coordenadas);
    if (opciones.domContent) {
      popup.setDOMContent(opciones.domContent);
    } else if (opciones.html !== undefined) {
      popup.setHTML(opciones.html);
    }
    popup.addTo(this.mapa);

    if (!opciones.allowMultiple) {
      this.popupActivo = popup;
      popup.on('close', () => {
        if (this.popupActivo === popup) this.popupActivo = undefined;
        opciones.onClose?.();
      });
    } else {
      popup.on('close', () => opciones.onClose?.());
    }
    return popup;
  }

  /** El popup abierto por `mostrarPopup`, para quien tenga que refrescarlo. */
  public get popupAbierto(): mapboxgl.Popup | undefined {
    return this.popupActivo;
  }

  public cerrarPopupActivo(): void {
    if (this.popupActivo) {
      this.popupActivo.remove();
      this.popupActivo = undefined;
    }
  }

  private leerTemaInicial(): Tema {
    return localStorage.getItem('temaOscuro') === 'true' ? 'dark' : 'light';
  }

  private aplicarEstilo(): void {
    if (!this.mapa) return;
    const estilo = MapboxService.resolverEstilo(this.vista, this.tema);
    this.mapa.setStyle(estilo);
  }

  private async reaplicarEstadoEnEstilo(): Promise<void> {
    if (!this.mapa) return;
    // setStyle resetea config props del basemap → forzamos reaplicar todo.
    this.configBasemapAplicado = {};
    this.aplicarLightPreset();
    this.aplicarLocaleAR();
    this.aplicarConfigBasemap(true);
    // Solo los pins canvas (sincrónicos, sin red). Los PNGs legacy se
    // resuelven on-demand vía styleimagemissing.
    this.iconosService.registrarPinTematico(this.mapa);
    const snapshot = Array.from(this.capasActivas.values()).map(
      (e) => e.config,
    );
    this.capasActivas.clear();
    for (const c of snapshot) {
      // Aislamos cada capa: si una falla (fetch KMZ caído, etc.) no debe
      // abortar el re-agregado de las demás.
      try {
        await this.agregarCapa(c);
      } catch (e) {
        console.error(`[MapaBase] no se pudo reaplicar capa ${c.id}:`, e);
      }
    }
  }

  // Auto preset según tema (day/night). Si el padre pasa `lightPreset`
  // explícito en `configBasemap`, ese gana — skipeamos el auto.
  private aplicarLightPreset(): void {
    if (!this.mapa || this.vista !== 'default') return;
    if (this.configBasemap['lightPreset'] !== undefined) return;
    try {
      (
        this.mapa as unknown as {
          setConfigProperty: (
            importId: string,
            key: string,
            value: string,
          ) => void;
        }
      ).setConfigProperty(
        'basemap',
        'lightPreset',
        this.tema === 'dark' ? 'night' : 'day',
      );
    } catch {
      /* el estilo no soporta basemap config */
    }
  }

  // Cache del último config aplicado: solo setConfigProperty para keys que
  // realmente cambiaron de valor. Sin esto, cada ngOnChanges (1+ por
  // session) vuelve a setear todas las props → trigger de tile re-render.
  private configBasemapAplicado: Record<string, unknown> = {};

  // Aplica solo el diff vs último estado aplicado. Falla por-prop
  // silenciosamente: Standard Satellite no acepta `theme`/`show3dObjects`,
  // pero los show*Labels y lightPreset sí.
  private aplicarConfigBasemap(forzar = false): void {
    if (!this.mapa) return;
    const m = this.mapa as unknown as {
      setConfigProperty?: (
        importId: string,
        key: string,
        value: unknown,
      ) => void;
    };
    for (const [k, v] of Object.entries(this.configBasemap)) {
      if (!forzar && this.configBasemapAplicado[k] === v) continue;
      try {
        m.setConfigProperty?.('basemap', k, v);
        this.configBasemapAplicado[k] = v;
      } catch {
        /* prop no soportada en este estilo */
      }
    }
  }

  // Forzar locale español + worldview Argentina. Esto cambia los nombres
  // territorialmente sensibles según la perspectiva argentina:
  //   - "Islas Malvinas" en lugar de "Falkland Islands"
  //   - Etiquetas de la Antártida según reclamación AR
  // Ver https://docs.mapbox.com/help/glossary/worldview/
  // En Mapbox Standard se setea via config properties; en otros estilos
  // (satellite-streets-v12, etc.) Mapbox v3 expone setLanguage/setWorldview
  // como métodos directos.
  private aplicarLocaleAR(): void {
    if (!this.mapa) return;
    const m = this.mapa as unknown as {
      setConfigProperty?: (
        importId: string,
        key: string,
        value: string,
      ) => void;
      setLanguage?: (lang: string) => void;
      setWorldview?: (wv: string) => void;
    };
    try {
      m.setConfigProperty?.('basemap', 'language', 'es');
      m.setConfigProperty?.('basemap', 'worldview', 'AR');
    } catch {
      /* estilo sin basemap config */
    }
    try {
      m.setLanguage?.('es');
      m.setWorldview?.('AR');
    } catch {
      /* método no disponible en este estilo / versión */
    }
  }

  // Encola una mutación del mapa detrás de las pendientes. Garantiza que
  // sincronizarCapas y reaplicarEstadoEnEstilo nunca corran interleaved.
  private encolarSync(fn: () => Promise<void>): void {
    this.colaSync = this.colaSync
      .then(() => fn())
      .catch((e) => console.error('[MapaBase] error en sync de capas:', e));
  }

  // Reintenta la sincronización hasta que el estilo esté listo. Acotado para no
  // quedar girando si el estilo nunca carga (por ejemplo, sin red).
  private reintentarSincronizarCapas(): void {
    if (this.reintentosEstilo >= MAX_REINTENTOS_ESTILO) {
      console.error(
        '[MapaBase] el estilo no terminó de cargar: capas sin sincronizar',
      );
      return;
    }
    this.reintentosEstilo++;
    setTimeout(
      () => this.encolarSync(() => this.sincronizarCapas()),
      ESPERA_ESTILO_MS,
    );
  }

  private async sincronizarCapas(): Promise<void> {
    if (!this.mapa) return;
    // No usar `once('style.load', ...)` ni `once('styledata', ...)`: los dos
    // dependen de que quede al menos un evento por disparar. Si entramos acá
    // justo después de que el estilo terminó de cargar pero isStyleLoaded()
    // todavía devuelve false (Mapbox lo deja en false mientras termina sprites
    // y sources), no llega ningún evento más, el reintento nunca corre y el
    // mapa se queda sin capas: el padre tampoco vuelve a emitir `capas` si la
    // data no cambió. Por eso se pollea con reintentos acotados.
    if (!this.mapa.isStyleLoaded()) {
      this.reintentarSincronizarCapas();
      return;
    }
    this.reintentosEstilo = 0;

    this.iconosService.registrarPinTematico(this.mapa);

    const idsNuevos = new Set(this.capas.map((c) => c.id));
    for (const id of Array.from(this.capasActivas.keys())) {
      if (!idsNuevos.has(id)) {
        this.removerCapa(id);
      }
    }

    for (const config of this.capas) {
      // Aislamos cada capa: una que falle no debe abortar el resto del loop
      // (antes un addSource duplicado en 'dispositivos' impedía que se
      // agregaran las capas KMZ que venían después).
      try {
        const existente = this.capasActivas.get(config.id);
        if (!existente) {
          await this.agregarCapa(config);
        } else if (this.cambioRequiereRecrear(existente.config, config)) {
          this.removerCapa(config.id);
          await this.agregarCapa(config);
        } else {
          this.actualizarDataCapa(existente, config);
          existente.config = config;
        }
      } catch (e) {
        console.error(
          `[MapaBase] no se pudo sincronizar capa ${config.id}:`,
          e,
        );
      }
    }
    this.reordenarPorPrioridad();
  }

  // Reordena layers por `prioridad` (mayor = más arriba). map.moveLayer(id)
  // sin segundo arg manda al tope; iterando en orden ASC, la última prioridad
  // queda arriba. Idempotente y barato.
  private reordenarPorPrioridad(): void {
    if (!this.mapa) return;
    const ordenados = Array.from(this.capasActivas.values()).sort(
      (a, b) => (a.config.prioridad ?? 0) - (b.config.prioridad ?? 0),
    );
    for (const estado of ordenados) {
      for (const lid of estado.layerIds) {
        if (this.mapa.getLayer(lid)) this.mapa.moveLayer(lid);
      }
    }
  }

  private cambioRequiereRecrear(prev: ConfigCapa, next: ConfigCapa): boolean {
    if (prev.tipo !== next.tipo) return true;
    if (JSON.stringify(prev.estilo) !== JSON.stringify(next.estilo))
      return true;
    if (JSON.stringify(prev.cluster) !== JSON.stringify(next.cluster))
      return true;
    if (prev.visible !== next.visible) return true;
    // KMZ: si capa.id es el mismo, asumimos misma KMZ aunque la URL signed
    // del backend cambie entre polls (S3/GCS suelen rotar tokens). Evita
    // refetch + flicker en cada polling.
    return false;
  }

  private actualizarDataCapa(
    estado: CapaInternaEstado,
    nueva: ConfigCapa,
  ): void {
    if (!this.mapa || nueva.tipo === 'kmz') return;
    const nuevaData = nueva.data as GeoJSON.FeatureCollection;
    for (const sourceId of estado.sourceIds) {
      const src = this.mapa.getSource(sourceId) as
        mapboxgl.GeoJSONSource | undefined;
      src?.setData(nuevaData);
    }
  }

  private async agregarCapa(config: ConfigCapa): Promise<void> {
    if (!this.mapa) return;
    if (config.visible === false) {
      this.capasActivas.set(config.id, {
        config,
        sourceIds: [],
        layerIds: [],
        handlers: [],
      });
      return;
    }

    switch (config.tipo) {
      case 'puntos':
        this.agregarPuntos(config);
        break;
      case 'cluster-puntos':
        this.agregarClusterPuntos(config);
        break;
      case 'linea':
        this.agregarLineas(config);
        break;
      case 'poligono':
        this.agregarPoligonos(config);
        break;
      case 'heatmap':
        this.agregarHeatmap(config);
        break;
      case 'kmz':
        await this.agregarKmz(config);
        break;
    }
  }

  private agregarPuntos(config: ConfigCapa): void {
    const map = this.mapa!;
    const sourceId = `${config.id}-src`;
    const layerId = `${config.id}-layer`;

    map.addSource(sourceId, {
      type: 'geojson',
      data: config.data as GeoJSON.FeatureCollection,
      // Solo puntos: sin geometrías que crucen bordes de tile no hace falta
      // buffer (default 128). Recomendación oficial de Mapbox para sources
      // de puntos — menos trabajo por tile en pan/zoom.
      buffer: 0,
    });

    const usaIcono = !!config.estilo?.icono;
    const layerIds: string[] = [];
    let idCirculoCompacto: string | undefined;
    if (usaIcono) {
      const circulo = this.buildCirculoCompacto(
        layerId,
        sourceId,
        config,
        undefined,
      );
      if (circulo) {
        map.addLayer(circulo as never);
        idCirculoCompacto = circulo['id'] as string;
        layerIds.push(idCirculoCompacto);
      }
      const symbolLayers = this.buildSymbolLayers(
        layerId,
        sourceId,
        config,
        undefined,
      );
      for (const l of symbolLayers) {
        map.addLayer(l as never);
        layerIds.push(l['id'] as string);
      }
    } else {
      map.addLayer({
        id: layerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-color': (config.estilo?.color ?? '#1976d2') as never,
          'circle-radius': (config.estilo?.radio ?? 6) as never,
          'circle-stroke-color': config.estilo?.colorBorde ?? '#ffffff',
          'circle-stroke-width': config.estilo?.grosorBorde ?? 1,
          'circle-opacity': config.estilo?.opacidad ?? 1,
          'circle-emissive-strength': 1,
        } as never,
      });
      layerIds.push(layerId);
    }

    const estado: CapaInternaEstado = {
      config,
      sourceIds: [sourceId],
      layerIds,
      handlers: [],
    };
    // Click handler en el layer "topmost" (donde está el texto), que es
    // donde realmente se ven los pins.
    this.engancharInteraccion(estado, layerIds[layerIds.length - 1]);
    // El círculo compacto y el pin tienen rangos de zoom disjuntos: nunca
    // se ven los dos a la vez, así que el click no se duplica.
    if (idCirculoCompacto) this.engancharInteraccion(estado, idCirculoCompacto);
    this.capasActivas.set(config.id, estado);
  }

  // Círculo chico que reemplaza al icono por debajo de `estilo.zoomIcono`
  // (ver EstiloCapa). Undefined si la capa no lo pide.
  private buildCirculoCompacto(
    layerIdBase: string,
    sourceId: string,
    config: ConfigCapa,
    extraFilter: unknown[] | undefined,
  ): Record<string, unknown> | undefined {
    const e = config.estilo!;
    if (e.zoomIcono === undefined) return undefined;
    const layer: Record<string, unknown> = {
      id: `${layerIdBase}-compacto`,
      type: 'circle',
      source: sourceId,
      maxzoom: e.zoomIcono,
      paint: {
        'circle-color': (e.colorIcono ?? e.color ?? '#1976d2') as never,
        'circle-radius': (e.radio ?? 5) as never,
        'circle-stroke-color': e.colorBorde ?? '#ffffff',
        'circle-stroke-width': e.grosorBorde ?? 1,
        'circle-opacity': e.opacidad ?? 1,
        'circle-stroke-opacity': e.opacidad ?? 1,
        'circle-emissive-strength': 1,
      },
    };
    if (extraFilter) layer['filter'] = extraFilter;
    return layer;
  }

  // Devuelve uno o dos symbol layers según el config.
  // - Layer base: icono principal (típicamente teardrop SDF tintado por estado).
  // - Layer secundario (opcional): iconoSecundario (ej. disco blanco) +
  //   text-field. Va arriba del primero, mismo source y mismo anchor para que
  //   alineen visualmente.
  private buildSymbolLayers(
    layerIdBase: string,
    sourceId: string,
    config: ConfigCapa,
    extraFilter: unknown[] | undefined,
  ): Record<string, unknown>[] {
    const e = config.estilo!;
    const anchor = e.iconoAnchor ?? 'bottom';
    const tieneCapaTexto =
      e.iconoSecundario !== undefined || e.texto !== undefined;

    const layers: Record<string, unknown>[] = [];

    // Layer 1: icono base (sin texto). Si hay texto pero NO hay
    // iconoSecundario, vamos a un solo layer (icono + texto).
    const layer1: Record<string, unknown> = {
      id:
        tieneCapaTexto && e.iconoSecundario !== undefined
          ? `${layerIdBase}-base`
          : layerIdBase,
      type: 'symbol',
      source: sourceId,
      layout: {
        'icon-image': e.icono as never,
        'icon-size': (e.tamanioIcono ?? 1) as never,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': anchor,
      },
      paint: {
        'icon-opacity': e.opacidad ?? 1,
        'icon-emissive-strength': 1,
        ...(e.colorIcono !== undefined ? { 'icon-color': e.colorIcono } : {}),
      },
    };
    if (extraFilter) layer1['filter'] = extraFilter;
    // Con zoomIcono, por debajo de ese zoom se ve el círculo compacto.
    if (e.zoomIcono !== undefined) layer1['minzoom'] = e.zoomIcono;

    // Si NO hay iconoSecundario pero sí texto, el texto se agrega al layer
    // base (single-layer fallback).
    if (e.iconoSecundario === undefined && e.texto !== undefined) {
      this.agregarTextoALayer(layer1, e);
    }
    layers.push(layer1);

    // Layer 2: iconoSecundario + texto. Stackeado encima del layer base.
    if (e.iconoSecundario !== undefined) {
      const layer2: Record<string, unknown> = {
        id: `${layerIdBase}-disco`,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': e.iconoSecundario as never,
          'icon-size': (e.tamanioIcono ?? 1) as never,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': anchor,
        },
        paint: {
          'icon-opacity': e.opacidad ?? 1,
          'icon-emissive-strength': 1,
          ...(e.colorIconoSecundario !== undefined
            ? { 'icon-color': e.colorIconoSecundario }
            : {}),
        },
      };
      if (e.texto !== undefined) this.agregarTextoALayer(layer2, e);
      if (extraFilter) layer2['filter'] = extraFilter;
      if (e.zoomIcono !== undefined) layer2['minzoom'] = e.zoomIcono;
      layers.push(layer2);
    }

    return layers;
  }

  // Helper compartido: setea text-field + paint + halo en el layer indicado.
  // Usa fuentes que están en TODOS los estilos de Mapbox (Standard, Streets,
  // Light, Dark, Satellite-Streets) — sin esto, en Standard a veces el text
  // no renderea por glyph faltante.
  private agregarTextoALayer(
    layer: Record<string, unknown>,
    e: EstiloCapa,
  ): void {
    const layout = layer['layout'] as Record<string, unknown>;
    layout['text-field'] = e.texto;
    layout['text-size'] = e.tamanioTexto ?? 13;
    layout['text-font'] = ['DIN Pro Bold', 'Arial Unicode MS Bold'];
    layout['text-anchor'] = 'center';
    layout['text-justify'] = 'center';
    layout['text-allow-overlap'] = true;
    layout['text-ignore-placement'] = true;
    if (e.offsetTexto) layout['text-offset'] = e.offsetTexto;

    const paint = layer['paint'] as Record<string, unknown>;
    paint['text-color'] = e.colorTexto ?? '#ffffff';
    paint['text-emissive-strength'] = 1;
    // Halo blanco/negro para que la letra sea legible incluso si el disco
    // se descentra o el color de fondo coincide con el del texto.
    paint['text-halo-color'] = 'rgba(255,255,255,0.85)';
    paint['text-halo-width'] = 1.2;
  }

  private agregarClusterPuntos(config: ConfigCapa): void {
    const map = this.mapa!;
    const sourceId = `${config.id}-src`;
    const idClusters = `${config.id}-clusters`;
    const idCount = `${config.id}-cluster-count`;
    const idUnclustered = `${config.id}-unclustered`;

    const c = config.cluster ?? {};
    const colores = c.coloresCirculo ?? [
      '#51bbd6',
      100,
      '#f1f075',
      750,
      '#f28cb1',
    ];
    const radios = c.radiosCirculo ?? [20, 100, 30, 750, 40];

    const sourceConfig: Record<string, unknown> = {
      type: 'geojson',
      data: config.data as GeoJSON.FeatureCollection,
      cluster: true,
      clusterRadius: c.radio ?? 50,
      clusterMaxZoom: c.maxZoom ?? 14,
      // Source de solo puntos → sin buffer (ver agregarPuntos).
      buffer: 0,
    };
    if (c.propiedades && Object.keys(c.propiedades).length > 0) {
      sourceConfig['clusterProperties'] = c.propiedades;
    }
    map.addSource(sourceId, sourceConfig as never);

    const colorCirculo = c.expresionColor ?? [
      'step',
      ['get', 'point_count'],
      colores[0],
      colores[1],
      colores[2],
      colores[3],
      colores[4],
    ];

    map.addLayer({
      id: idClusters,
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': colorCirculo as never,
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          radios[0],
          radios[1],
          radios[2],
          radios[3],
          radios[4],
        ] as never,
        // Mapbox Standard usa lighting 3D — sin emissive-strength los círculos
        // quedan apagados en lightPreset 'night'. 1.0 = sin atenuación.
        'circle-emissive-strength': 1,
      } as never,
    });

    map.addLayer({
      id: idCount,
      type: 'symbol',
      source: sourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'] as never,
        'text-size': 12,
      },
      paint: {
        'text-color': (c.colorTexto ?? '#000000') as never,
        'text-emissive-strength': 1,
      } as never,
    });

    const usaIcono = !!config.estilo?.icono;
    const idsUnclustered: string[] = [];
    let idCirculoCompacto: string | undefined;
    if (usaIcono) {
      const circulo = this.buildCirculoCompacto(
        idUnclustered,
        sourceId,
        config,
        ['!', ['has', 'point_count']],
      );
      if (circulo) {
        map.addLayer(circulo as never);
        idCirculoCompacto = circulo['id'] as string;
        idsUnclustered.push(idCirculoCompacto);
      }
      const symbolLayers = this.buildSymbolLayers(
        idUnclustered,
        sourceId,
        config,
        ['!', ['has', 'point_count']],
      );
      for (const l of symbolLayers) {
        map.addLayer(l as never);
        idsUnclustered.push(l['id'] as string);
      }
    } else {
      map.addLayer({
        id: idUnclustered,
        type: 'circle',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': (config.estilo?.color ?? '#1976d2') as never,
          'circle-radius': (config.estilo?.radio ?? 6) as never,
          'circle-stroke-color': config.estilo?.colorBorde ?? '#ffffff',
          'circle-stroke-width': config.estilo?.grosorBorde ?? 1,
        },
      });
      idsUnclustered.push(idUnclustered);
    }

    const estado: CapaInternaEstado = {
      config,
      sourceIds: [sourceId],
      layerIds: [idClusters, idCount, ...idsUnclustered],
      handlers: [],
    };

    this.engancharClickCluster(estado, idClusters, sourceId);
    // Click handler en el layer "topmost" del unclustered (si hay disco
    // blanco, ése; si no, el base).
    this.engancharInteraccion(
      estado,
      idsUnclustered[idsUnclustered.length - 1],
    );
    // Rangos de zoom disjuntos con el pin (ver agregarPuntos).
    if (idCirculoCompacto) this.engancharInteraccion(estado, idCirculoCompacto);
    this.capasActivas.set(config.id, estado);
  }

  private agregarLineas(config: ConfigCapa): void {
    const map = this.mapa!;
    const sourceId = `${config.id}-src`;
    const layerId = `${config.id}-layer`;

    map.addSource(sourceId, {
      type: 'geojson',
      data: config.data as GeoJSON.FeatureCollection,
    });
    map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': (config.estilo?.color ?? '#f80') as never,
        'line-width': (config.estilo?.grosorLinea ?? 3) as never,
        'line-opacity': config.estilo?.opacidad ?? 1,
        'line-emissive-strength': 1,
      } as never,
    });

    const estado: CapaInternaEstado = {
      config,
      sourceIds: [sourceId],
      layerIds: [layerId],
      handlers: [],
    };
    this.engancharInteraccion(estado, layerId);
    this.capasActivas.set(config.id, estado);
  }

  private agregarPoligonos(config: ConfigCapa): void {
    const map = this.mapa!;
    const sourceId = `${config.id}-src`;
    const idFill = `${config.id}-fill`;
    const idLine = `${config.id}-line`;

    map.addSource(sourceId, {
      type: 'geojson',
      data: config.data as GeoJSON.FeatureCollection,
    });
    map.addLayer({
      id: idFill,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': (config.estilo?.color ??
          'rgba(25, 118, 210, 0.3)') as never,
        'fill-opacity': config.estilo?.opacidad ?? 0.5,
        'fill-emissive-strength': 1,
      } as never,
    });
    map.addLayer({
      id: idLine,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': config.estilo?.colorBorde ?? '#1976d2',
        'line-width': config.estilo?.grosorBorde ?? 2,
        'line-emissive-strength': 1,
      } as never,
    });

    const estado: CapaInternaEstado = {
      config,
      sourceIds: [sourceId],
      layerIds: [idFill, idLine],
      handlers: [],
    };
    this.engancharInteraccion(estado, idFill);
    this.capasActivas.set(config.id, estado);
  }

  // Capa heatmap nativa de Mapbox. El `weight` de cada punto sale de
  // estilo.pesoHeatmap (típicamente una expresión que normaliza una métrica
  // como maxRssi). El color sale de estilo.rampaHeatmap (pares [stop, color]
  // sobre heatmap-density). Pensado para densidad de cobertura: a zoom bajo
  // muestra el "calor" agregado; a zoom alto se desvanece (zoomMaximoHeatmap)
  // dando paso a los puntos individuales.
  private agregarHeatmap(config: ConfigCapa): void {
    const map = this.mapa!;
    const sourceId = `${config.id}-src`;
    const layerId = `${config.id}-layer`;
    const e = config.estilo ?? {};

    map.addSource(sourceId, {
      type: 'geojson',
      data: config.data as GeoJSON.FeatureCollection,
    });

    // Rampa de color sobre heatmap-density. El stop 0 SIEMPRE debe ser
    // transparente (requisito de Mapbox: density 0 = sin dato). Los stops
    // del usuario se aplican a partir de ahí.
    const rampa = e.rampaHeatmap ?? [
      [0.2, 'rgba(0,0,255,0.5)'],
      [0.4, 'rgb(0,255,255)'],
      [0.6, 'rgb(0,255,0)'],
      [0.8, 'rgb(255,255,0)'],
      [1, 'rgb(255,0,0)'],
    ];
    const colorExpr: unknown[] = [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0,
      'rgba(0,0,0,0)',
    ];
    for (const [stop, color] of normalizarRampaHeatmap(
      rampa as Array<[number, string]>,
    )) {
      colorExpr.push(stop, color);
    }

    const radio = e.radioHeatmap ?? 30;
    const intensidad = e.intensidadHeatmap ?? 1;
    const peso = e.pesoHeatmap ?? 1;

    const paint: Record<string, unknown> = {
      'heatmap-weight': peso,
      'heatmap-intensity': intensidad,
      'heatmap-color': colorExpr,
      'heatmap-radius': radio,
      'heatmap-opacity': e.opacidad ?? 0.85,
    };
    // Fade-out por zoom: a partir de zoomMaximoHeatmap el heatmap se vuelve
    // transparente para que los puntos individuales tomen protagonismo.
    if (e.zoomMaximoHeatmap !== undefined) {
      paint['heatmap-opacity'] = [
        'interpolate',
        ['linear'],
        ['zoom'],
        e.zoomMaximoHeatmap - 1,
        e.opacidad ?? 0.85,
        e.zoomMaximoHeatmap,
        0,
      ];
    }

    const layer: Record<string, unknown> = {
      id: layerId,
      type: 'heatmap',
      source: sourceId,
      paint,
    };
    if (e.zoomMaximoHeatmap !== undefined) {
      layer['maxzoom'] = e.zoomMaximoHeatmap;
    }
    map.addLayer(layer as never);

    const estado: CapaInternaEstado = {
      config,
      sourceIds: [sourceId],
      layerIds: [layerId],
      handlers: [],
    };
    this.capasActivas.set(config.id, estado);
  }

  private async agregarKmz(config: ConfigCapa): Promise<void> {
    const map = this.mapa!;
    const data = config.data as { urlKmz: string; urlGeoJson?: string };

    let fc: GeoJSON.FeatureCollection;
    try {
      // config.id como cache key: estable entre polls aunque la signed URL
      // del backend rote. Re-toggles y cambios de vista no re-parsean.
      // Preferimos el GeoJSON pre-convertido server-side (fetch+parse JSON
      // directo); KMZs anteriores a la feature caen al pipeline KMZ.
      fc = data.urlGeoJson
        ? await this.kmzService.cargarGeoJson(data.urlGeoJson, config.id)
        : await this.kmzService.cargar(data.urlKmz, config.id);
    } catch (err) {
      console.error(`[MapaBase] No se pudo cargar KMZ ${config.id}:`, err);
      this.capasActivas.set(config.id, {
        config,
        sourceIds: [],
        layerIds: [],
        handlers: [],
      });
      this.kmzCargada.emit(config.id);
      return;
    }

    const sourceId = `${config.id}-src`;
    const idFill = `${config.id}-fill`;
    const idLine = `${config.id}-line`;
    const idPoint = `${config.id}-point`;

    map.addSource(sourceId, { type: 'geojson', data: fc });

    map.addLayer({
      id: idFill,
      type: 'fill',
      source: sourceId,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': (config.estilo?.color ??
          'rgba(25, 118, 210, 0.25)') as never,
        'fill-opacity': config.estilo?.opacidad ?? 0.5,
        'fill-outline-color': config.estilo?.colorBorde ?? '#1976d2',
        'fill-emissive-strength': 1,
      } as never,
    });
    map.addLayer({
      id: idLine,
      type: 'line',
      source: sourceId,
      filter: [
        'in',
        ['geometry-type'],
        ['literal', ['LineString', 'MultiLineString']],
      ],
      paint: {
        'line-color': (config.estilo?.color ?? '#1976d2') as never,
        'line-width': (config.estilo?.grosorLinea ?? 2) as never,
        'line-emissive-strength': 1,
      } as never,
    });
    map.addLayer({
      id: idPoint,
      type: 'circle',
      source: sourceId,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-color': (config.estilo?.color ?? '#1976d2') as never,
        // Default 8px (antes 5px) para que sean fáciles de clickear y de
        // diferenciar visualmente. Mapbox usa el radio renderizado como
        // hit-target del click handler.
        'circle-radius': (config.estilo?.radio ?? 8) as never,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
        'circle-emissive-strength': 1,
      } as never,
    });

    const estado: CapaInternaEstado = {
      config,
      sourceIds: [sourceId],
      layerIds: [idFill, idLine, idPoint],
      handlers: [],
    };
    this.engancharInteraccionKmz(estado, [idFill, idLine, idPoint]);
    this.capasActivas.set(config.id, estado);
    this.kmzCargada.emit(config.id);
  }

  private engancharInteraccion(
    estado: CapaInternaEstado,
    layerId: string,
  ): void {
    const map = this.mapa!;
    const interaccion = estado.config.interaccion;
    if (!interaccion) return;

    const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
      if (this.clickConsumidoPorOtraCapa(e, estado.config.id)) return;
      const feature = e.features?.[0];
      if (!feature) return;
      if (interaccion.popupHtml) {
        const coord =
          feature.geometry.type === 'Point'
            ? (feature.geometry.coordinates as [number, number])
            : [e.lngLat.lng, e.lngLat.lat];
        this.mostrarPopup({
          coordenadas: coord as [number, number],
          html: interaccion.popupHtml(feature.properties ?? {}),
        });
      }
      if (interaccion.onClick) interaccion.onClick(feature);
      this.featureClick.emit({ capaId: estado.config.id, feature });
    };

    const onEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = interaccion.cursor ?? 'pointer';
      const feature = e.features?.[0];
      if (!interaccion.popupAlPasar || !interaccion.popupHtml || !feature)
        return;
      const coord =
        feature.geometry.type === 'Point'
          ? (feature.geometry.coordinates as [number, number])
          : [e.lngLat.lng, e.lngLat.lat];
      this.mostrarPopup({
        coordenadas: coord as [number, number],
        html: interaccion.popupHtml(feature.properties ?? {}),
      });
    };
    const onLeave = () => (map.getCanvas().style.cursor = '');

    map.on('click', layerId, onClick);
    map.on('mouseenter', layerId, onEnter);
    map.on('mouseleave', layerId, onLeave);

    estado.handlers.push(
      { tipo: 'click', layerId, fn: onClick as never },
      { tipo: 'mouseenter', layerId, fn: onEnter as never },
      { tipo: 'mouseleave', layerId, fn: onLeave as never },
    );
  }

  private engancharInteraccionKmz(
    estado: CapaInternaEstado,
    layerIds: string[],
  ): void {
    const map = this.mapa!;
    for (const layerId of layerIds) {
      const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
        if (this.clickConsumidoPorOtraCapa(e, estado.config.id)) return;
        const f = e.features?.[0];
        if (!f) return;
        const props = f.properties ?? {};
        const html = this.renderPopupKmz(props as Record<string, unknown>);
        const coord =
          f.geometry.type === 'Point'
            ? (f.geometry.coordinates as [number, number])
            : [e.lngLat.lng, e.lngLat.lat];
        this.mostrarPopup({
          coordenadas: coord as [number, number],
          html,
        });
        this.featureClick.emit({ capaId: estado.config.id, feature: f });
      };
      // Cambiar cursor a pointer al hover, sólo si no hay un dispositivo
      // arriba (que ya tiene su propio handler con prioridad).
      const onEnter = (e: mapboxgl.MapLayerMouseEvent) => {
        if (this.clickConsumidoPorOtraCapa(e, estado.config.id)) return;
        map.getCanvas().style.cursor = 'pointer';
      };
      const onLeave = () => {
        map.getCanvas().style.cursor = '';
      };

      map.on('click', layerId, onClick);
      map.on('mouseenter', layerId, onEnter);
      map.on('mouseleave', layerId, onLeave);
      estado.handlers.push(
        { tipo: 'click', layerId, fn: onClick as never },
        { tipo: 'mouseenter', layerId, fn: onEnter as never },
        { tipo: 'mouseleave', layerId, fn: onLeave as never },
      );
    }
  }

  // Mapbox dispara handlers de click independientes por layer: si un click
  // cae sobre un dispositivo y una capa KMZ a la vez, ambos popups se abrían.
  // Resolvemos por prioridad: dispositivos (puntos/cluster-puntos) > KMZ.
  // Si la capa actual es KMZ y hay un dispositivo en el mismo punto, ignorar.
  private clickConsumidoPorOtraCapa(
    e: mapboxgl.MapLayerMouseEvent,
    capaIdActual: string,
  ): boolean {
    if (!this.mapa) return false;
    const capaActual = this.capasActivas.get(capaIdActual);
    if (!capaActual || capaActual.config.tipo !== 'kmz') return false;

    const layersPrioritarios: string[] = [];
    for (const [id, est] of this.capasActivas) {
      if (id === capaIdActual) continue;
      if (est.config.tipo === 'kmz') continue;
      layersPrioritarios.push(...est.layerIds);
    }
    const existentes = layersPrioritarios.filter((l) => this.mapa!.getLayer(l));
    if (existentes.length === 0) return false;
    const features = this.mapa.queryRenderedFeatures(e.point, {
      layers: existentes,
    });
    return features.length > 0;
  }

  private engancharClickCluster(
    estado: CapaInternaEstado,
    layerId: string,
    sourceId: string,
  ): void {
    const map = this.mapa!;
    const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
      this.cerrarPopupActivo();
      const features = map.queryRenderedFeatures(e.point, {
        layers: [layerId],
      });
      const f = features[0];
      if (!f) return;
      const clusterId = (f.properties as { cluster_id: number }).cluster_id;
      const src = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
      src.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || zoom === null || zoom === undefined) return;
        map.easeTo({
          center: (f.geometry as GeoJSON.Point).coordinates as [number, number],
          zoom,
        });
      });
    };
    const onEnter = () => (map.getCanvas().style.cursor = 'pointer');
    const onLeave = () => (map.getCanvas().style.cursor = '');

    map.on('click', layerId, onClick);
    map.on('mouseenter', layerId, onEnter);
    map.on('mouseleave', layerId, onLeave);

    estado.handlers.push(
      { tipo: 'click', layerId, fn: onClick as never },
      { tipo: 'mouseenter', layerId, fn: onEnter as never },
      { tipo: 'mouseleave', layerId, fn: onLeave as never },
    );
  }

  private removerCapa(id: string): void {
    const estado = this.capasActivas.get(id);
    if (!estado || !this.mapa) return;
    for (const h of estado.handlers) {
      this.mapa.off(h.tipo, h.layerId, h.fn as never);
    }
    for (const lid of estado.layerIds) {
      if (this.mapa.getLayer(lid)) this.mapa.removeLayer(lid);
    }
    for (const sid of estado.sourceIds) {
      if (this.mapa.getSource(sid)) this.mapa.removeSource(sid);
    }
    this.capasActivas.delete(id);
  }

  private crearMarker(coord: [number, number], draggable: boolean): void {
    if (!this.mapa) return;
    this.marker = new mapboxgl.Marker({ draggable })
      .setLngLat(coord)
      .addTo(this.mapa);
    if (draggable) {
      this.marker.on('dragend', () => {
        const ll = this.marker!.getLngLat();
        this.coordenadaCambiada.emit([ll.lng, ll.lat]);
      });
    }
  }

  private escaparHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private renderPopupKmz(props: Record<string, unknown>): string {
    const name = this.extraerString(props['name']);
    const description = this.normalizarDescripcionKmz(props['description']);

    const tieneHtml = description && /<\w+[^>]*>/.test(description);
    const cuerpo = tieneHtml
      ? description
      : description
        ? `<p>${this.escaparHtml(description)}</p>`
        : this.renderTablaPropsKmz(props);

    return `
      <div style="padding: 12px 14px; font-family: inherit;">
        ${name ? `<h3 style="margin:0 0 8px;font-size:14px;">${this.escaparHtml(name)}</h3>` : ''}
        <div style="font-size:12px; line-height:1.4;">${cuerpo}</div>
      </div>`;
  }

  // togeojson a veces devuelve description envuelta en {@type:'html', value:'...'}
  // o como JSON string del mismo objeto. Extrae el texto/HTML real.
  private normalizarDescripcionKmz(raw: unknown): string {
    if (!raw) return '';
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed.startsWith('{') && trimmed.includes('"@type"')) {
        try {
          const obj = JSON.parse(trimmed);
          return this.extraerString(obj?.value);
        } catch {
          /* fallback al string crudo */
        }
      }
      return trimmed;
    }
    if (typeof raw === 'object') {
      const v = (raw as { value?: unknown }).value;
      if (v) return this.extraerString(v);
    }
    return this.extraerString(raw);
  }

  private extraerString(v: unknown): string {
    if (typeof v === 'string') return v;
    if (v == null) return '';
    return String(v);
  }

  // Fallback cuando no hay description: tabla key/value de propiedades del feature.
  private renderTablaPropsKmz(props: Record<string, unknown>): string {
    const filas: string[] = [];
    for (const [k, v] of Object.entries(props)) {
      if (k === 'name' || k === 'description' || v == null || v === '') {
        continue;
      }
      filas.push(
        `<tr><td style="padding:2px 6px;color:#666;">${this.escaparHtml(k)}</td>` +
          `<td style="padding:2px 6px;">${this.escaparHtml(this.extraerString(v))}</td></tr>`,
      );
    }
    if (filas.length === 0) return '';
    return `<table style="border-collapse:collapse;font-size:12px;">${filas.join('')}</table>`;
  }
}
